/**
 * UI labels and copy for Requirement Analysis only.
 * Keep domain-neutral: no product-specific keys, IDs, or sample user stories.
 */

export const HEALTH_STATUS_LABELS = {
  good: 'Good',
  needs_clarification: 'Needs clarification',
  incomplete: 'Incomplete',
  contradictory: 'Contradictory',
  risky: 'Risky',
};

export const SIGNAL_VALUE_LABELS = {
  good: 'Good',
  partial: 'Partial',
  poor: 'Poor',
  missing: 'Missing',
  not_needed: 'Not needed',
  matched: 'Matched',
  mismatch_found: 'Mismatch found',
  design_gap_found: 'Design gap found',
  visual_not_available: 'No design available',
  no_relevant_design: 'No relevant design found',
  not_checked: 'Not checked',
};

export function formatSignalValue(value) {
  if (value == null || value === '') return '—';
  return SIGNAL_VALUE_LABELS[value] || String(value);
}

/** Backend joins tabular gap findings with this delimiter (see requirement_analysis rules). */
const TABULAR_CLARIFICATIONS_DELIM = '\n\nClarifications needed:\n';

/**
 * Parse a material finding whose body uses "Clarifications needed:" with "-" lines.
 * Returns null if the string does not match that shape (other findings render as plain text).
 */
export function parseMaterialFindingBody(raw) {
  if (raw == null || typeof raw !== 'string') return null;
  const text = raw.replace(/\r\n/g, '\n');
  const idx = text.indexOf(TABULAR_CLARIFICATIONS_DELIM);
  if (idx === -1) return null;
  const title = text.slice(0, idx).trim();
  const rest = text.slice(idx + TABULAR_CLARIFICATIONS_DELIM.length).trim();
  const bullets = rest
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => /^-\s+/.test(line))
    .map((line) => line.replace(/^-\s+/, '').trim())
    .filter(Boolean);
  if (!title || bullets.length === 0) return null;
  return { title, bullets };
}

const TABULAR_SUMMARY_HEALTH = new Set(['needs_clarification', 'incomplete']);

const TABULAR_SUMMARY_LEAD =
  'The main requirement is clear, but important user-facing behaviors are not yet specified.';

const TABULAR_SUMMARY_NEUTRAL_TAIL =
  'The story describes the main table/list behavior, but the acceptance criteria do not yet define important states, scale behavior, ordering, or action behavior.';

function normalizeSummaryNewlines(s) {
  return String(s || '').replace(/\r\n/g, '\n').trim();
}

/**
 * When deterministic tabular/list gaps fired and health is not "good", use a fixed two-paragraph summary so
 * the narrative matches Partial signals and Important findings (no contradictory LLM praise retained).
 */
export function displayRequirementSummaryText(summary, data) {
  const text = summary == null ? '' : String(summary);
  const triggered = data?.diagnostics?.rules_triggered || [];
  const health = data?.requirement_health?.status;
  if (!health || !TABULAR_SUMMARY_HEALTH.has(health)) return text;
  if (!Array.isArray(triggered) || !triggered.includes('tabular_list_material_gaps')) return text;

  const combined = `${TABULAR_SUMMARY_LEAD}\n\n${TABULAR_SUMMARY_NEUTRAL_TAIL}`;
  const s = normalizeSummaryNewlines(text);
  if (!s) return combined;
  if (s === combined) return combined;
  return combined;
}
export const STORY_VISUAL_DETAIL_KEYS = [
  'mismatches',
  'visual_implied_requirements',
  'missing_story_detail_from_visual',
  'optional_visual_state_clarifications',
];

const STORY_VISUAL_DETAIL_TITLES = {
  mismatches: 'Mismatches',
  visual_implied_requirements: 'Visual implied requirements',
  missing_story_detail_from_visual: 'Missing story detail from visual',
  optional_visual_state_clarifications: 'Optional visual state clarifications',
};

export function titleForStoryVisualDetailKey(key) {
  return STORY_VISUAL_DETAIL_TITLES[key] || String(key).replace(/_/g, ' ');
}

/** Findings list exists and has at least one item. */
export function hasMaterialFindings(data) {
  return Array.isArray(data?.findings) && data.findings.length > 0;
}

/** Any story↔visual detail array has content (not status-only noise). */
export function storyVisualSectionHasDetailContent(storyVisual) {
  if (!storyVisual || typeof storyVisual !== 'object') return false;
  return STORY_VISUAL_DETAIL_KEYS.some((k) => {
    const arr = storyVisual[k];
    return Array.isArray(arr) && arr.length > 0;
  });
}

/**
 * Show diagnostics only when there is non-default debug signal
 * (rules fired, parse failure, or linked visual sources inspected).
 */
export function shouldShowDiagnosticsPanel(diagnostics) {
  if (!diagnostics || typeof diagnostics !== 'object') return false;
  const rules = diagnostics.rules_triggered;
  if (Array.isArray(rules) && rules.length > 0) return true;
  if (diagnostics.llm_parse_failed === true) return true;
  const v = diagnostics.visual_sources_checked;
  return typeof v === 'number' && v > 0;
}

export const REQUIREMENT_ANALYSIS_COPY = {
  modalTitle: 'Requirement Analysis',
  cachedResultLabel: 'Cached result',
  analyzedAtPrefix: 'Analyzed',
  refreshAnalysis: 'Re-analyze',
  modalSubtitle:
    'Checks clarity, completeness, edge cases, user-facing gaps, and story/design consistency.',
  analyzingPrefix: 'Analyzing',
  summaryCardTitle: 'Summary',
  summaryRequirementHealthPrefix: 'Requirement Health:',
  summaryClarityPrefix: 'Clarity:',
  summaryCompletenessPrefix: 'Completeness:',
  summaryAcPrefix: 'Acceptance Criteria:',
  summaryUserPerspectivePrefix: 'User Perspective:',
  summaryVisualPrefix: 'Visual Consistency:',
  summaryBlockerPrefix: 'Blocker:',
  summaryImportantPrefix: 'Important:',
  summaryOptionalPrefix: 'Optional:',
  summaryConfidencePrefix: 'Confidence:',
  confidenceReasonPrefix: 'Confidence:',
  sectionRequirementSummary: 'Requirement summary',
  sectionMaterialFindings: 'Material findings',
  sectionOpenQuestions: 'Open questions',
  sectionSuggestedAc: 'Suggested acceptance criteria',
  sectionNegativeEdge: 'Negative and edge scenarios',
  sectionUserPerspective: 'User perspective review',
  sectionStoryVisual: 'Story ↔ Visual Consistency',
  sectionAssumptions: 'Assumptions',
  sectionDiagnostics: 'Diagnostics',
  storyVisualStatusPrefix: 'Status:',
  emptyNoFindings: 'No material findings for this scope.',
  emptyNoneListed: 'None listed.',
  emptyNoneSuggested: 'None suggested.',
  emptyNoVisualDeltas: 'No detailed visual deltas recorded.',
  emptyNoAnalysisData: 'No analysis data.',
  close: 'Close',
  defaultItemTitle: 'Requirement',
  findingEvidencePrefix: 'Evidence:',
  findingWhyPrefix: 'Why it matters:',
  findingRecommendationPrefix: 'Recommendation:',
  findingClarificationsHeading: 'Clarifications needed:',
  findingCategoryFallback: 'finding',
  diagRulesTriggered: 'Rules triggered',
  diagVisualSourcesChecked: 'Visual sources checked',
  diagLlmUsed: 'LLM used',
  diagLlmParsedSuccessfully: 'LLM output parsed successfully',
  diagPipelineVersion: 'Pipeline version',
  diagRequestId: 'Request ID',
  diagDurationMs: 'Duration (ms)',
  diagYes: 'Yes',
  diagNo: 'No',
  diagNotApplicable: '—',
  diagValueNone: '—',
  diagEmDash: '—',
};

export function buildDiagnosticsRows(data) {
  const C = REQUIREMENT_ANALYSIS_COPY;
  const d = data?.diagnostics || {};
  const rows = [];
  const rules = d.rules_triggered;
  rows.push({
    label: C.diagRulesTriggered,
    value: Array.isArray(rules) && rules.length > 0 ? rules.join(', ') : C.diagValueNone,
  });
  rows.push({
    label: C.diagVisualSourcesChecked,
    value: String(d.visual_sources_checked ?? 0),
  });

  const llmUsed = d.llm_used === true;
  rows.push({
    label: C.diagLlmUsed,
    value: llmUsed ? C.diagYes : C.diagNo,
  });
  rows.push({
    label: C.diagLlmParsedSuccessfully,
    value: !llmUsed ? C.diagNotApplicable : d.llm_parse_failed ? C.diagNo : C.diagYes,
  });
  rows.push({
    label: C.diagPipelineVersion,
    value: data.pipeline_version ?? C.diagEmDash,
  });
  rows.push({
    label: C.diagRequestId,
    value: data.request_id ?? C.diagEmDash,
  });
  rows.push({
    label: C.diagDurationMs,
    value: data.duration_ms != null ? `${data.duration_ms}` : C.diagEmDash,
  });
  return rows;
}

export const REQUIREMENT_ANALYSIS_ACCORDION_SX = {
  mb: 1,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  '&:before': { display: 'none' },
  boxShadow: 'none',
  '& .MuiAccordionDetails-root': {
    borderTop: '1px solid',
    borderColor: 'divider',
  },
};

export function chipColorForFindingSeverity(severity) {
  if (severity === 'blocker') return 'error';
  if (severity === 'important') return 'warning';
  return 'default';
}
